import { BrowserDataCacheSourceAdapter } from './source-adapter';
import { BrowserDataCacheQuery, BrowserDataCacheQueryResult, BrowserDataCacheSyncJob, BrowserDataCacheSyncOptions } from './types';
import {
  BrowserDataCacheRuntime,
  BrowserDataCacheRuntimeCandidate,
  BrowserDataCacheRuntimeSelection,
  selectBrowserDataCacheRuntime,
} from './runtime';
import { createLiveNoCacheRuntimeCandidate, LiveNoCacheRuntime } from '../runtime/live-no-cache';

export interface BrowserDataCacheOptions {
  sources: BrowserDataCacheSourceAdapter<any, any, any>[];
  runtimes?: BrowserDataCacheRuntimeCandidate[];
}

export class BrowserDataCache {
  private readonly runtime: BrowserDataCacheRuntime;
  readonly selectedRuntimeId: string;
  readonly runtimeProbes: BrowserDataCacheRuntimeSelection['probes'];

  constructor(
    runtime: BrowserDataCacheRuntime,
    selectedRuntimeId: string = runtime.capabilities.runtime,
    runtimeProbes: BrowserDataCacheRuntimeSelection['probes'] = [],
  ) {
    this.runtime = runtime;
    this.selectedRuntimeId = selectedRuntimeId;
    this.runtimeProbes = runtimeProbes;
  }

  static async create(options: BrowserDataCacheOptions): Promise<BrowserDataCache> {
    const selection = await selectBrowserDataCacheRuntime([
      ...(options.runtimes || []),
      createLiveNoCacheRuntimeCandidate({ sources: options.sources }),
    ]);
    return new BrowserDataCache(selection.runtime, selection.selectedId, selection.probes);
  }

  capabilities() {
    return this.runtime.capabilities;
  }

  startSync(sourceId: string, options?: BrowserDataCacheSyncOptions): BrowserDataCacheSyncJob {
    return this.runtime.startSync(sourceId, options);
  }

  query<TRow = unknown>(query: BrowserDataCacheQuery): Promise<BrowserDataCacheQueryResult<TRow>> {
    return this.runtime.query<TRow>(query);
  }

  upsertRows(table: string, rows: unknown[]): Promise<void> {
    return this.runtime.upsertRows(table, rows);
  }
}

export function createBrowserDataCache(options: BrowserDataCacheOptions): BrowserDataCache {
  const runtime = new LiveNoCacheRuntime({ sources: options.sources });
  return new BrowserDataCache(runtime, 'live-no-cache', [{
    id: 'live-no-cache',
    result: {
      ok: true,
      capabilities: runtime.capabilities,
    },
  }]);
}

export function createBrowserDataCacheAsync(options: BrowserDataCacheOptions): Promise<BrowserDataCache> {
  return BrowserDataCache.create(options);
}
